import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { DashboardLayout } from '../components/Layout';

const ClientBillingPage = () => {
    const [activeTab, setActiveTab] = useState('account');
    const [account, setAccount] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);

    const getCreditsValue = (credits) => {
        if (credits == null) return 0;
        if (typeof credits === 'number') return credits;
        if (typeof credits === 'object') {
            return credits.availableCredits ?? credits.totalCreditsPurchased ?? 0;
        }
        const parsed = Number(credits);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        // Mark billing as seen so the sidebar badge reflects only new pending docs
        try {
            localStorage.setItem('seen.billingAt', new Date().toISOString());
            window.dispatchEvent(new Event('updateBadges'));
        } catch {
            // ignore
        }
    }, []);

    const loadData = async () => {
        try {
            const [accountRes, invoicesRes, receiptsRes] = await Promise.all([
                api.get('/billing/my/account').catch(() => ({ data: {} })),
                api.get('/billing/my/invoices').catch(() => ({ data: [] })),
                api.get('/billing/my/receipts').catch(() => ({ data: [] }))
            ]);
            setAccount(accountRes.data);
            setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
            setReceipts(Array.isArray(receiptsRes.data) ? receiptsRes.data : []);
        } catch (error) {
            console.error('Erro:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Paid': return { bg: 'var(--success-bg)', color: 'var(--success-text)', label: '✅ Paga' };
            case 'Issued': return { bg: 'var(--info-bg)', color: 'var(--info)', label: '📄 Emitida' };
            case 'Overdue': return { bg: 'var(--error-bg)', color: 'var(--error)', label: '⚠️ Vencida' };
            case 'Cancelled': return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', label: '❌ Cancelada' };
            case 'Draft': return { bg: 'var(--warning-bg)', color: 'var(--warning)', label: '📝 Rascunho' };
            default: return { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)', label: status };
        }
    };

    const openHtmlPreview = async (endpoint) => {
        try {
            const res = await api.get(endpoint, { responseType: 'text' });
            const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            toast.error('Não foi possível abrir o documento');
        }
    };

    const openPdfDocument = async (endpoint, fallbackHtmlEndpoint) => {
        try {
            const res = await api.get(endpoint, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            if (fallbackHtmlEndpoint) {
                await openHtmlPreview(fallbackHtmlEndpoint);
            } else {
                toast.error('Não foi possível abrir o documento');
            }
        }
    };

    if (loading) {
        return (
            <DashboardLayout userType="client">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <div className="spinner"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userType="client">
            <div style={styles.container}>
                <div style={styles.header}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                        <div>
                            <h1 style={styles.title}>📄 Faturas & Recibos</h1>
                            <p style={styles.subtitle}>A sua conta corrente</p>
                        </div>
                        <button
                            style={{
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border-color)',
                                borderRadius: 10,
                                padding: '10px 12px',
                                fontWeight: 900,
                                cursor: 'pointer',
                                color: 'var(--text-primary)'
                            }}
                            onClick={() => {
                                try {
                                    localStorage.setItem('seen.billingAt', new Date().toISOString());
                                    window.dispatchEvent(new Event('updateBadges'));
                                    toast.success('Faturas & recibos marcados como vistos');
                                } catch {
                                    // ignore
                                }
                            }}
                        >
                            ✓ Marcar como visto
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div style={styles.tabs}>
                    {[
                        { id: 'account', label: '💼 Conta Corrente' },
                        { id: 'invoices', label: `📄 Faturas (${invoices.length})` },
                        { id: 'receipts', label: `🧾 Recibos (${receipts.length})` },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Account Summary */}
                {activeTab === 'account' && (
                    <div>
                        {/* Balance Summary */}
                        <div style={styles.kpiGrid}>
                            <div style={styles.kpiCard}>
                                <span style={{ fontSize: '2rem' }}>💰</span>
                                <div>
                                    <div style={styles.kpiLabel}>Total Faturado</div>
                                    <div style={styles.kpiValue}>{(account?.totalInvoiced || 0).toFixed(2)}€</div>
                                </div>
                            </div>
                            <div style={styles.kpiCard}>
                                <span style={{ fontSize: '2rem' }}>✅</span>
                                <div>
                                    <div style={styles.kpiLabel}>Total Pago</div>
                                    <div style={{ ...styles.kpiValue, color: 'var(--success-text)' }}>{(account?.totalPaid || 0).toFixed(2)}€</div>
                                </div>
                            </div>
                            <div style={styles.kpiCard}>
                                <span style={{ fontSize: '2rem' }}>⏳</span>
                                <div>
                                    <div style={styles.kpiLabel}>Saldo Pendente</div>
                                    <div style={{ ...styles.kpiValue, color: (account?.pendingBalance || 0) > 0 ? '#dc2626' : '#16a34a' }}>
                                        {(account?.pendingBalance || 0).toFixed(2)}€
                                    </div>
                                </div>
                            </div>
                            <div style={styles.kpiCard}>
                                <span style={{ fontSize: '2rem' }}>🪙</span>
                                <div>
                                    <div style={styles.kpiLabel}>Créditos</div>
                                    <div style={{ ...styles.kpiValue, color: 'var(--accent-primary)' }}>{getCreditsValue(account?.credits)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Subscription */}
                        {account?.subscription && (
                            <div style={styles.card}>
                                <h3>📋 Subscrição Ativa</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--success-bg)', borderRadius: '10px' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Plano</div>
                                        <div style={{ fontWeight: '700' }}>{account.subscription.planName || 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--success-bg)', borderRadius: '10px' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Valor Mensal</div>
                                        <div style={{ fontWeight: '700', color: 'var(--success-text)' }}>{(account.subscription.monthlyPrice || 0).toFixed(2)}€/mês</div>
                                    </div>
                                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--info-bg)', borderRadius: '10px' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Início</div>
                                        <div style={{ fontWeight: '600' }}>
                                            {account.subscription.startDate ? new Date(account.subscription.startDate).toLocaleDateString('pt-PT') : '-'}
                                        </div>
                                    </div>
                                    <div style={{ padding: '0.75rem', backgroundColor: 'var(--info-bg)', borderRadius: '10px' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Próxima Renovação</div>
                                        <div style={{ fontWeight: '600' }}>
                                            {account.subscription.nextRenewal ? new Date(account.subscription.nextRenewal).toLocaleDateString('pt-PT') : '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Recent Invoices */}
                        <div style={styles.card}>
                            <h3>📄 Últimas Faturas</h3>
                            {invoices.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>Sem faturas</p>
                            ) : (
                                <div style={{ marginTop: '0.5rem' }}>
                                    {invoices.slice(0, 5).map(inv => {
                                        const statusInfo = getStatusBadge(inv.status);
                                        return (
                                            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--info)' }}>
                                                <div>
                                                    <strong>{inv.invoiceNumber}</strong>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                        {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('pt-PT') : ''}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: statusInfo.bg, color: statusInfo.color }}>
                                                        {statusInfo.label}
                                                    </span>
                                                    <strong>{(inv.total || 0).toFixed(2)}€</strong>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Recent Receipts */}
                        <div style={styles.card}>
                            <h3>🧾 Últimos Recibos</h3>
                            {receipts.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>Sem recibos</p>
                            ) : (
                                <div style={{ marginTop: '0.5rem' }}>
                                    {receipts.slice(0, 5).map(rec => (
                                        <div key={rec.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--info)' }}>
                                            <div>
                                                <strong>{rec.receiptNumber}</strong>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                    {rec.paymentDate ? new Date(rec.paymentDate).toLocaleDateString('pt-PT') : ''}
                                                    {rec.paymentMethod && ` • ${rec.paymentMethod}`}
                                                </div>
                                            </div>
                                            <strong style={{ color: 'var(--success-text)' }}>{(rec.amount || 0).toFixed(2)}€</strong>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Invoices Tab */}
                {activeTab === 'invoices' && (
                    <div>
                        {invoices.length === 0 ? (
                            <div style={styles.emptyState}>
                                <span style={{ fontSize: '3rem' }}>📄</span>
                                <h3>Sem faturas</h3>
                                <p>Ainda não tem faturas emitidas.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {invoices.map(inv => {
                                    const statusInfo = getStatusBadge(inv.status);
                                    return (
                                        <div key={inv.id} style={styles.invoiceCard}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <strong style={{ fontSize: '1.0625rem' }}>{inv.invoiceNumber}</strong>
                                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                                                        <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: statusInfo.bg, color: statusInfo.color }}>
                                                            {statusInfo.label}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{(inv.total || 0).toFixed(2)}€</div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                        IVA: {(inv.taxAmount || 0).toFixed(2)}€
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                                                <span>📅 Emitida: {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('pt-PT') : '-'}</span>
                                                <span>⏰ Vencimento: {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('pt-PT') : '-'}</span>
                                                {inv.paymentMethod && <span>💳 {inv.paymentMethod}</span>}
                                                <button style={styles.viewBtn} onClick={() => openPdfDocument(`/billing/my/invoices/${inv.id}/pdf`, `/billing/my/invoices/${inv.id}/print`)}>Ver PDF</button>
                                            </div>
                                            {inv.lines && inv.lines.length > 0 && (
                                                <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--info)', paddingTop: '0.5rem' }}>
                                                    {inv.lines.map((line, idx) => (
                                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', padding: '0.25rem 0' }}>
                                                            <span>{line.description}</span>
                                                            <span>{line.quantity}x {(line.unitPrice || 0).toFixed(2)}€ = {(line.total || 0).toFixed(2)}€</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Receipts Tab */}
                {activeTab === 'receipts' && (
                    <div>
                        {receipts.length === 0 ? (
                            <div style={styles.emptyState}>
                                <span style={{ fontSize: '3rem' }}>🧾</span>
                                <h3>Sem recibos</h3>
                                <p>Ainda não tem recibos emitidos.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {receipts.map(rec => (
                                    <div key={rec.id} style={styles.invoiceCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong style={{ fontSize: '1.0625rem' }}>{rec.receiptNumber}</strong>
                                                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                                    Ref. Fatura: {rec.invoiceNumber || '-'}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--success-text)' }}>{(rec.amount || 0).toFixed(2)}€</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                                            <span>📅 {rec.paymentDate ? new Date(rec.paymentDate).toLocaleDateString('pt-PT') : '-'}</span>
                                            <span>💳 {rec.paymentMethod || '-'}</span>
                                            {rec.transactionReference && <span>🔗 {rec.transactionReference}</span>}
                                            <button style={styles.viewBtn} onClick={() => openPdfDocument(`/billing/my/receipts/${rec.id}/pdf`, `/billing/my/receipts/${rec.id}/print`)}>Ver PDF</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

const styles = {
    container: { padding: '1rem' },
    header: { marginBottom: '1rem' },
    title: { margin: 0, fontSize: '1.5rem' },
    subtitle: { margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' },
    tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' },
    tab: { padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' },
    tabActive: { backgroundColor: 'var(--accent-primary)', color: 'white' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' },
    kpiCard: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    kpiLabel: { fontSize: '0.75rem', color: 'var(--text-secondary)' },
    kpiValue: { fontSize: '1.5rem', fontWeight: '700' },
    card: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1rem' },
    invoiceCard: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    emptyState: { textAlign: 'center', padding: '3rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    viewBtn: { border: '1px solid var(--info)', backgroundColor: 'var(--info-bg)', borderRadius: '8px', padding: '4px 10px', fontSize: '0.75rem', cursor: 'pointer' },
};

export default ClientBillingPage;

